import { useEffect } from 'react'
import { AlertCircle, AlertTriangle, Info, X, RefreshCw } from 'lucide-react'
// Unused: CheckCircle
import { cn } from '@/lib/utils'
import { ErrorState } from '@/hooks/useErrorHandler'

interface ToastProps {
  error: ErrorState
  onClose: () => void
  className?: string
}

export function Toast({ error, onClose, className }: ToastProps) {
  useEffect(() => {
    // Auto-dismiss after certain time based on type
    const timeouts = {
      error: 0, // Don't auto-dismiss errors
      warning: 10000,
      info: 5000
    }

    if (timeouts[error.type] > 0) {
      const timer = setTimeout(onClose, timeouts[error.type])
      return () => clearTimeout(timer)
    }
  }, [error.type, onClose])

  const getIcon = () => {
    switch (error.type) {
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-400" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-400" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-400" />
      default:
        return <Info className="h-5 w-5 text-blue-400" />
    }
  }

  const getStyles = () => {
    switch (error.type) {
      case 'error':
        return 'bg-red-50 border-red-200'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200'
      case 'info':
        return 'bg-blue-50 border-blue-200'
      default:
        return 'bg-blue-50 border-blue-200'
    }
  }

  const getTextStyles = () => {
    switch (error.type) {
      case 'error':
        return 'text-red-800'
      case 'warning':
        return 'text-yellow-800'
      case 'info':
        return 'text-blue-800'
      default:
        return 'text-blue-800'
    }
  }

  return (
    <div className={cn(
      "flex items-start p-4 border rounded-md shadow-sm max-w-md",
      getStyles(),
      className
    )}>
      <div className="flex-shrink-0">
        {getIcon()}
      </div>

      <div className="ml-3 flex-1">
        <p className={cn("text-sm font-medium", getTextStyles())}>
          {error.message}
        </p>

        {error.retry && (
          <div className="mt-2">
            <button
              onClick={error.retry}
              className={cn(
                "inline-flex items-center px-3 py-1 text-xs font-medium rounded-md",
                error.type === 'error'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : error.type === 'warning'
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              )}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </button>
          </div>
        )}
      </div>

      <div className="ml-4 flex-shrink-0 flex">
        <button
          onClick={onClose}
          className={cn(
            "inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2",
            error.type === 'error'
              ? 'text-red-400 hover:text-red-600 focus:ring-red-500'
              : error.type === 'warning'
              ? 'text-yellow-400 hover:text-yellow-600 focus:ring-yellow-500'
              : 'text-blue-400 hover:text-blue-600 focus:ring-blue-500'
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

interface ToastContainerProps {
  errors: ErrorState[]
  onClose: (index: number) => void
  className?: string
}

export function ToastContainer({ errors, onClose, className }: ToastContainerProps) {
  if (errors.length === 0) return null

  return (
    <div className={cn("fixed top-4 right-4 z-50 space-y-2", className)}>
      {errors.map((error, index) => (
        <Toast
          key={error.timestamp}
          error={error}
          onClose={() => onClose(index)}
        />
      ))}
    </div>
  )
}
